const BASE = process.env.REACT_APP_API_BASE_URL;

function authHeaders(json = true) {
  const token = localStorage.getItem("token");
  const h = { Authorization: `Bearer ${token}` };
  if (json) h["Content-Type"] = "application/json";
  return h;
}

export function extractArray(raw) {
  let arr = [];
  if (!raw) arr = [];
  else if (Array.isArray(raw)) arr = raw;
  else if (Array.isArray(raw.data)) arr = raw.data;

  // Never surface soft-deleted patients to the UI.
  return arr.filter((item) => !item?.is_delete);
}

export function extractObject(raw) {
  if (!raw) return null;
  if (raw.data && typeof raw.data === "object" && !Array.isArray(raw.data)) return raw.data;
  if (typeof raw === "object" && !Array.isArray(raw) && raw.patient_id) return raw;
  return null;
}

export async function registerInquiry(payload) {
  const res = await fetch(`${BASE}/api/program/registeration`, {
    method:  "POST",
    headers: authHeaders(true),
    body:    JSON.stringify({ registration_type: "inquiry", ...payload }),
  });
  if (!res.ok) {
    const err = await res.json().catch(() => ({}));
    throw new Error(err.message || `Registration failed: ${res.status}`);
  }
  return res.json();
}

export async function registerConnection({ patientId, programId }) {
  const res = await fetch(`${BASE}/api/program/registeration`, {
    method:  "POST",
    headers: authHeaders(true),
    body:    JSON.stringify({
      registration_type: "connection",
      patient_id:        patientId,
      program_id:        programId,
    }),
  });
  if (!res.ok) {
    const err = await res.json().catch(() => ({}));
    throw new Error(err.message || `Connection failed: ${res.status}`);
  }
  return res.json();
}

export async function listProgramPatients() {
  const params = new URLSearchParams({ view: "task_list" });
  const res = await fetch(`${BASE}/api/program/listpatients?${params}`, {
    method:  "GET",
    headers: authHeaders(false),
  });
  if (!res.ok) throw new Error(`List patients failed: ${res.status}`);
  return res.json();
}

export async function listNcdPatients() {
  const params = new URLSearchParams({ view: "ncd_data" });
  const res = await fetch(`${BASE}/api/program/listpatients?${params}`, {
    method:  "GET",
    headers: authHeaders(false),
  });
  if (!res.ok) throw new Error(`List NCD patients failed: ${res.status}`);
  return res.json();
}

export async function fetchPatientProgram(patientId) {
  const params = new URLSearchParams({ patient_id: patientId, view: "task_list" });
  const res = await fetch(`${BASE}/api/program/listpatients?${params}`, {
    method:  "GET",
    headers: authHeaders(false),
  });
  if (!res.ok) throw new Error(`Fetch program failed: ${res.status}`);
  return res.json();
}

/* ─────────────────────────────────────────────────────────────
   Bulk-save task updates for a specific program day.

   updates[] items use "task_id" as the field name because that
   is what TaskUpdateItem expects on the backend — but the value
   passed must be the instance_key (e.g. "P2_S1_T1__d5"), not
   the template id. CareJourney already stores state by
   instance_key so Object.entries(taskState) gives the right keys.
───────────────────────────────────────────────────────────── */
export async function updateProgramTasks({ tasklistId, currentPhase, programDay, updates }) {
  const res = await fetch(`${BASE}/api/program/update`, {
    method:  "PATCH",
    headers: authHeaders(true),
    body:    JSON.stringify({
      tasklist_id:   tasklistId,
      current_phase: currentPhase,
      program_day:   programDay,   // ← now included so backend knows which day
      updates,
    }),
  });
  if (!res.ok) throw new Error(`Update tasks failed: ${res.status}`);
  return res.json();
}

/* ─────────────────────────────────────────────────────────────
   Upload / replace a patient's diet plan (PDF).

   PATCH /api/program/update-diet
   Multipart form-data fields: patient_id, program_id, diet_pdf (the PDF).
   The backend reports both patient_id and program_id as required.
   NOTE: no Content-Type header is set, so the browser adds the
   multipart boundary itself.
───────────────────────────────────────────────────────────── */
export async function uploadDietPlan({ patientId, programId, file }) {
  if (!patientId) throw new Error("uploadDietPlan requires patientId");
  if (!file)      throw new Error("uploadDietPlan requires a file");

  // Backend validation expects patient_id & program_id in the BODY (form fields), not the query.
  const formData = new FormData();
  formData.append("patient_id", patientId);
  if (programId) formData.append("program_id", programId);
  formData.append("diet_pdf", file);

  const res = await fetch(`${BASE}/api/program/update-diet`, {
    method:  "PATCH",             // POST gave 405; if PATCH also fails, try "PUT"
    headers: authHeaders(false),  // no JSON Content-Type for file uploads
    body:    formData,
  });

  if (!res.ok) {
    const err = await res.json().catch(() => ({}));
    throw new Error(err.message || err.detail || `Diet upload failed: ${res.status}`);
  }

  return res.json().catch(() => ({}));
}

/* ─────────────────────────────────────────────────────────────
   Build a viewable URL for a stored diet PDF.

   The API returns only a storage key, e.g.
   "prescription/<patient_id>/<timestamp>_<name>.pdf".
   - If the value is already a full http(s) URL, it is used as-is.
   - Otherwise it is appended to REACT_APP_LAB_REPORT_URL (the S3
     bucket base; REACT_APP_FILE_BASE_URL is a fallback). If neither
     is set, this returns null.
   If your backend serves these files via a signed-URL endpoint,
   replace the body of this function with a call to that endpoint.
───────────────────────────────────────────────────────────── */
export function getDietPdfUrl(key) {
  if (!key) return null;
  if (/^https?:\/\//i.test(key)) return key;

  // Same S3 bucket base that is already used for lab reports.
  const fileBase = process.env.REACT_APP_LAB_REPORT_URL || process.env.REACT_APP_FILE_BASE_URL;
  if (!fileBase) return null;

  // Encode each path segment separately so spaces, "—" and "#" in the file name are safe.
  const cleanKey = String(key)
    .replace(/^\/+/, "")
    .split("/")
    .map(encodeURIComponent)
    .join("/");

  return `${fileBase.replace(/\/+$/, "")}/${cleanKey}`;
}

/* ─────────────────────────────────────────────────────────────
   Delete a patient's program enrollment.

   patient_id and program_id are QUERY params (not a JSON body) —
   confirmed from the API spec: DELETE /api/program/delete?patient_id=...&program_id=...
   patient_id is required, program_id is optional.
───────────────────────────────────────────────────────────── */
export async function deletePatient({ patientId, programId }) {
  if (!patientId) {
    throw new Error("deletePatient requires patientId");
  }

  const params = new URLSearchParams({ patient_id: patientId });
  if (programId) params.set("program_id", programId);

  const res = await fetch(`${BASE}/api/program/delete?${params}`, {
    method:  "DELETE",
    headers: authHeaders(false), // no JSON body, so no Content-Type needed
  });

  if (!res.ok) {
    const err = await res.json().catch(() => ({}));
    throw new Error(err.message || `Delete patient failed: ${res.status}`);
  }

  return res.json().catch(() => ({}));
}